import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantSlug, setRestaurantSlug] = useState(null);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      const { items: savedItems, restaurantId: savedRestId, restaurantSlug: savedSlug } = JSON.parse(savedCart);
      setItems(savedItems || []);
      setRestaurantId(savedRestId);
      setRestaurantSlug(savedSlug);
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify({ items, restaurantId, restaurantSlug }));
  }, [items, restaurantId, restaurantSlug]);

  const addItem = (item, restId, restSlug) => {
    // If cart has items from different restaurant, clear it
    if (restaurantId && restaurantId !== restId) {
      const lineId = item.cart_line_id || `${item.id}-${Date.now()}`;
      setItems([{ ...item, cart_line_id: lineId, quantity: 1 }]);
      setRestaurantId(restId);
      setRestaurantSlug(restSlug);
      return;
    }

    setRestaurantId(restId);
    setRestaurantSlug(restSlug);

    setItems(prev => {
      // Two cart entries are mergeable only if same item AND same modifier selection.
      // We derive a deterministic key from modifiers so identical configs merge.
      const modKey = (mods) => (mods || [])
        .map(m => `${m.group_name}:${m.option_name}`)
        .sort()
        .join('|');
      const incomingKey = modKey(item.modifiers);
      const existingIdx = prev.findIndex(i => i.id === item.id && modKey(i.modifiers) === incomingKey);
      if (existingIdx !== -1) {
        return prev.map((i, idx) => idx === existingIdx ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i);
      }
      const lineId = item.cart_line_id || `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      return [...prev, { ...item, cart_line_id: lineId, quantity: item.quantity || 1 }];
    });
  };

  const removeItem = (lineIdOrItemId) => {
    setItems(prev => prev.filter(i => (i.cart_line_id || i.id) !== lineIdOrItemId));
  };

  const updateQuantity = (lineIdOrItemId, quantity) => {
    if (quantity <= 0) {
      removeItem(lineIdOrItemId);
      return;
    }
    setItems(prev => prev.map(i => 
      (i.cart_line_id || i.id) === lineIdOrItemId ? { ...i, quantity } : i
    ));
  };

  const clearCart = () => {
    setItems([]);
    setRestaurantId(null);
    setRestaurantSlug(null);
    localStorage.removeItem('cart');
  };

  const getSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      items,
      restaurantId,
      restaurantSlug,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getSubtotal,
      getItemCount,
    }}>
      {children}
    </CartContext.Provider>
  );
};
